import React, { useState, useEffect } from 'react';
import {
  Car, Loader2, CheckCircle, XCircle, Edit2, Save, ChevronDown, ChevronUp, Image, Upload, Calendar, X, Download, Maximize2, Trash2, Plus
} from 'lucide-react';
import { api } from '../../services/api';

const VEHICLE_STATUS = {
  approved: { label: 'Freigegeben', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Ausstehend', cls: 'bg-amber-100 text-amber-700' },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' },
};
const TYPE_LABELS = { economy: 'Economy', comfort: 'Comfort', business: 'Business', premium: 'Premium', vip: 'VIP', suv: 'SUV', van: 'Van', minibus: 'Kleinbus', bus: 'Bus' };

export function DriverVehiclesTab({
  vehicles, expandedVehicle, setExpandedVehicle,
  editingVehicle, vehicleForm, setVehicleForm, startEditVehicle, saveVehicle,
  handleVehicleStatus, vehicleAction, saving, setEditingVehicle, driverId
}) {
  const [expiries, setExpiries] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [savingExpiry, setSavingExpiry] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { src, label } | null
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [addForm, setAddForm] = useState({
    make: '', model: '', year: new Date().getFullYear(), color: '',
    plate_number: '', vehicle_type: 'comfort', passengers: 4, luggage: 3,
    status: 'approved',
  });
  const atMaxVehicles = (vehicles?.length || 0) >= 4;

  const handleAddVehicle = async () => {
    if (!addForm.make || !addForm.model || !addForm.plate_number || !addForm.color) {
      alert('Bitte Marke, Modell, Farbe und Kennzeichen ausfüllen');
      return;
    }
    setAddingVehicle(true);
    try {
      await api.post(`/admin/driver/${driverId}/vehicle/add`, {
        ...addForm,
        year: Number(addForm.year) || new Date().getFullYear(),
        passengers: Number(addForm.passengers) || 4,
        luggage: Number(addForm.luggage) || 3,
      });
      setShowAddModal(false);
      window.location.reload();
    } catch (err) {
      alert('Fahrzeug hinzufügen fehlgeschlagen: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setAddingVehicle(false);
    }
  };

  useEffect(() => {
    if (driverId) loadExpiries();
  }, [driverId]);

  const loadExpiries = async () => {
    try {
      const res = await api.get(`/admin/driver/${driverId}/document-expiries`);
      setExpiries(res.data || []);
    } catch (e) { console.error(e); }
  };

  const getExpiry = (docType, vehicleId) => {
    const key = vehicleId ? `${docType}_${vehicleId}` : docType;
    return expiries.find(e => e.doc_key === key);
  };

  const handleFileUpload = async (docType, vehicleId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(`${docType}_${vehicleId || 'personal'}`);
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          await api.put(`/admin/driver/${driverId}/document`, {
            doc_type: docType,
            vehicle_id: vehicleId || null,
            file_data: reader.result,
          });
          window.location.reload();
        };
        reader.readAsDataURL(file);
      } catch (err) { alert('Upload fehlgeschlagen'); }
      finally { setUploading(null); }
    };
    input.click();
  };

  const handleFileDelete = async (docType, vehicleId) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;
    const key = `${docType}_${vehicleId || 'personal'}`;
    setUploading(key);
    try {
      await api.put(`/admin/driver/${driverId}/document`, {
        doc_type: docType,
        vehicle_id: vehicleId || null,
        file_data: '',
      });
      window.location.reload();
    } catch (err) {
      alert('Löschen fehlgeschlagen');
    } finally {
      setUploading(null);
    }
  };

  const handleExpiryChange = async (docType, vehicleId, date) => {
    const key = `${docType}_${vehicleId || 'personal'}`;
    setSavingExpiry(key);
    try {
      await api.put(`/admin/driver/${driverId}/document-expiry`, {
        doc_type: docType,
        vehicle_id: vehicleId || null,
        expiry_date: date,
      });
      loadExpiries();
    } catch (err) { alert('Fehler beim Speichern'); }
    finally { setSavingExpiry(null); }
  };

  const DocCard = ({ docKey, label, docVal, vehicleId }) => {
    const hasDoc = docVal && docVal !== 'pending' && docVal !== '';
    const isImage = hasDoc && (docVal.startsWith('data:') || docVal.startsWith('http'));
    const expiry = getExpiry(docKey, vehicleId);
    const expiryDate = expiry?.expiry_date ? expiry.expiry_date.slice(0, 10) : '';
    const isExpired = expiryDate && new Date(expiryDate) < new Date();
    const uploadKey = `${docKey}_${vehicleId || 'personal'}`;

    return (
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-600">{label}</span>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-medium ${hasDoc ? 'text-emerald-600' : 'text-slate-300'}`}>{hasDoc ? 'Vorhanden' : 'Fehlt'}</span>
            <button
              onClick={() => handleFileUpload(docKey, vehicleId)}
              disabled={uploading === uploadKey}
              className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-orange-500 transition-colors"
              title={hasDoc ? 'Dokument ersetzen' : 'Dokument hochladen'}
              data-testid={`upload-doc-${docKey}-${vehicleId || 'personal'}`}
            >
              {uploading === uploadKey ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            </button>
            {hasDoc && (
              <button
                onClick={() => handleFileDelete(docKey, vehicleId)}
                disabled={uploading === uploadKey}
                className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-600 transition-colors"
                title="Dokument löschen"
                data-testid={`delete-doc-${docKey}-${vehicleId || 'personal'}`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
        {isImage ? (
          <button
            type="button"
            onClick={() => setLightbox({ src: docVal, label })}
            className="block w-full relative group"
            data-testid={`view-doc-${docKey}-${vehicleId || 'personal'}`}
          >
            <img src={docVal} alt={label} className="w-full h-28 object-cover group-hover:opacity-80 transition-opacity" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity">
              <Maximize2 size={20} className="text-white" />
            </div>
          </button>
        ) : (
          <div className="h-28 flex items-center justify-center bg-slate-50"><Image size={20} className="text-slate-200" /></div>
        )}
        <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
          <div className="flex items-center gap-1.5">
            <Calendar size={10} className={isExpired ? 'text-red-500' : 'text-slate-400'} />
            <span className="text-[10px] text-slate-500">Gültig bis:</span>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => handleExpiryChange(docKey, vehicleId, e.target.value)}
              className={`text-[10px] px-1 py-0.5 border rounded w-28 ${isExpired ? 'border-red-300 text-red-600 bg-red-50' : 'border-slate-200 text-slate-600'}`}
              data-testid={`expiry-${docKey}-${vehicleId || 'personal'}`}
            />
            {savingExpiry === uploadKey && <Loader2 size={10} className="animate-spin text-orange-500" />}
          </div>
          {isExpired && <p className="text-[10px] text-red-500 mt-0.5 font-medium">Abgelaufen!</p>}
        </div>
      </div>
    );
  };
  return (
    <div data-testid="tab-content-vehicles">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-slate-800">Fahrzeuge ({vehicles.length})</h2>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={atMaxVehicles}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title={atMaxVehicles ? 'Maximum 4 Fahrzeuge erreicht' : 'Neues Fahrzeug hinzufügen'}
          data-testid="admin-add-vehicle-btn"
        >
          <Plus size={14} />
          Fahrzeug hinzufügen
        </button>
      </div>
      {vehicles.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><Car size={40} className="mx-auto mb-3 text-slate-200" /><p>Keine Fahrzeuge registriert</p></div>
      ) : (
        <div className="space-y-4">
          {vehicles.map((v, idx) => {
            const vStatus = VEHICLE_STATUS[v.status] || VEHICLE_STATUS.pending;
            const vDocs = v.documents || {};
            const isExpanded = expandedVehicle === v.id;
            const isEditingThis = editingVehicle === v.id;
            const vehiclePhoto = vDocs.vehicle_photo;
            const hasPhoto = vehiclePhoto && (vehiclePhoto.startsWith('data:') || vehiclePhoto.startsWith('http'));

            return (
              <div key={v.id || idx} className="border border-slate-200 rounded-2xl overflow-hidden" data-testid={`vehicle-card-${v.id}`}>
                <div className="px-5 py-3 bg-slate-50 flex items-center justify-between cursor-pointer" onClick={() => setExpandedVehicle(isExpanded ? null : v.id)}>
                  <div className="flex items-center gap-3">
                    <Car size={18} className="text-slate-400" />
                    <span className="font-semibold text-slate-800">{v.make} {v.model}</span>
                    <span className="text-xs text-slate-400">({v.plate_number})</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${vStatus.cls}`}>{vStatus.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {v.status !== 'approved' && (
                      <button onClick={e => { e.stopPropagation(); handleVehicleStatus(v.id, 'approved'); }} disabled={vehicleAction === v.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 disabled:opacity-50" data-testid={`approve-vehicle-${v.id}`}>
                        {vehicleAction === v.id ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle size={10} />} Freigeben
                      </button>
                    )}
                    {v.status !== 'rejected' && (
                      <button onClick={e => { e.stopPropagation(); handleVehicleStatus(v.id, 'rejected'); }} disabled={vehicleAction === v.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 disabled:opacity-50" data-testid={`reject-vehicle-${v.id}`}>
                        {vehicleAction === v.id ? <Loader2 size={10} className="animate-spin" /> : <XCircle size={10} />} Ablehnen
                      </button>
                    )}
                    {!isEditingThis && (
                      <button onClick={e => { e.stopPropagation(); startEditVehicle(v); }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600" data-testid={`edit-vehicle-${v.id}`}>
                        <Edit2 size={10} /> Bearbeiten
                      </button>
                    )}
                    {isExpanded ? <ChevronUp size={16} className="text-slate-400 ml-1" /> : <ChevronDown size={16} className="text-slate-400 ml-1" />}
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex gap-5">
                    <div className="w-48 h-32 rounded-xl overflow-hidden bg-slate-100 shrink-0 relative group">
                      {hasPhoto ? (
                        <button type="button" onClick={() => setLightbox({ src: vehiclePhoto, label: `${v.make} ${v.model}` })} className="w-full h-full block" data-testid={`view-vehicle-photo-${v.id}`}>
                          <img src={vehiclePhoto} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover hover:opacity-80 transition-opacity" />
                        </button>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Car size={32} className="text-slate-200" /></div>
                      )}
                      <button
                        onClick={() => handleFileUpload('vehicle_photo', v.id)}
                        disabled={uploading === `vehicle_photo_${v.id}`}
                        className="absolute bottom-2 right-2 p-1.5 bg-white/90 hover:bg-orange-50 border border-slate-200 rounded-lg shadow-sm text-slate-500 hover:text-orange-500 transition-colors"
                        title={hasPhoto ? 'Fahrzeugfoto ersetzen' : 'Fahrzeugfoto hochladen'}
                        data-testid={`upload-vehicle-photo-${v.id}`}
                      >
                        {uploading === `vehicle_photo_${v.id}` ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      </button>
                      {hasPhoto && (
                        <button
                          onClick={() => handleFileDelete('vehicle_photo', v.id)}
                          disabled={uploading === `vehicle_photo_${v.id}`}
                          className="absolute bottom-2 left-2 p-1.5 bg-white/90 hover:bg-red-50 border border-slate-200 rounded-lg shadow-sm text-slate-500 hover:text-red-600 transition-colors"
                          title="Fahrzeugfoto löschen"
                          data-testid={`delete-vehicle-photo-${v.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <div className="flex-1">
                      {isEditingThis ? (
                        <div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                            {[
                              { label: 'Hersteller', field: 'make' }, { label: 'Modell', field: 'model' },
                              { label: 'Baujahr', field: 'year', type: 'number' }, { label: 'Farbe', field: 'color' },
                              { label: 'Kennzeichen', field: 'plate_number' }, { label: 'Typ', field: 'vehicle_type' },
                              { label: 'Passagiere', field: 'passengers', type: 'number' }, { label: 'Gepäck', field: 'luggage', type: 'number' },
                            ].map(f => (
                              <div key={f.field}>
                                <span className="text-slate-400 text-xs block mb-1">{f.label}</span>
                                <input type={f.type || 'text'} value={vehicleForm[f.field] || ''} onChange={e => setVehicleForm(vf => ({ ...vf, [f.field]: e.target.value }))}
                                  className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                                  data-testid={`vedit-${f.field}-${v.id}`} />
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button onClick={() => saveVehicle(v.id)} disabled={saving}
                              className="flex items-center gap-1 px-4 py-2 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 disabled:opacity-50" data-testid={`save-vehicle-${v.id}`}>
                              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Speichern
                            </button>
                            <button onClick={() => setEditingVehicle(null)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200">Abbrechen</button>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div><span className="text-slate-400 text-xs block">Hersteller</span><p className="text-slate-800 font-medium">{v.make || '-'}</p></div>
                          <div><span className="text-slate-400 text-xs block">Modell</span><p className="text-slate-800 font-medium">{v.model || '-'}</p></div>
                          <div><span className="text-slate-400 text-xs block">Baujahr</span><p className="text-slate-800 font-medium">{v.year || '-'}</p></div>
                          <div><span className="text-slate-400 text-xs block">Farbe</span><p className="text-slate-800 font-medium">{v.color || '-'}</p></div>
                          <div><span className="text-slate-400 text-xs block">Kennzeichen</span><p className="text-slate-800 font-medium">{v.plate_number || '-'}</p></div>
                          <div><span className="text-slate-400 text-xs block">Typ</span><p className="text-slate-800 font-medium capitalize">{TYPE_LABELS[v.vehicle_type] || v.vehicle_type || '-'}</p></div>
                          <div><span className="text-slate-400 text-xs block">Passagiere</span><p className="text-slate-800 font-medium">{v.passengers || '-'}</p></div>
                          <div><span className="text-slate-400 text-xs block">Gepäck</span><p className="text-slate-800 font-medium">{v.luggage || '-'}</p></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vehicle Documents - always visible */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <span className="text-xs font-bold text-slate-600 mb-3 block">Fahrzeug-Dokumente</span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <DocCard docKey="vehicle_registration" label="Fahrzeugschein" docVal={vDocs.vehicle_registration} vehicleId={v.id} />
                      <DocCard docKey="vehicle_photo" label="Fahrzeugfoto" docVal={vDocs.vehicle_photo} vehicleId={v.id} />
                      <DocCard docKey="insurance" label="Versicherung" docVal={vDocs.insurance} vehicleId={v.id} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox modal for document images */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="doc-lightbox"
        >
          <div className="relative max-w-5xl w-full max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 text-white">
              <h3 className="text-base font-semibold">{lightbox.label}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={lightbox.src}
                  download={`${lightbox.label.replace(/\s+/g, '_')}.jpg`}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  title="Herunterladen"
                  data-testid="lightbox-download"
                  onClick={e => e.stopPropagation()}
                >
                  <Download size={18} />
                </a>
                <button
                  onClick={() => setLightbox(null)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  title="Schließen"
                  data-testid="lightbox-close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <img
              src={lightbox.src}
              alt={lightbox.label}
              className="w-full max-h-[85vh] object-contain rounded-lg bg-white"
            />
          </div>
        </div>
      )}

      {/* Add Vehicle Modal (admin fallback) */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !addingVehicle && setShowAddModal(false)}
          data-testid="admin-add-vehicle-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Neues Fahrzeug hinzufügen</h3>
              <button
                onClick={() => setShowAddModal(false)}
                disabled={addingVehicle}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"
                data-testid="admin-add-vehicle-close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Dokumente (Fahrzeugschein, Foto, Versicherung) können nach dem Erstellen über die Karte hochgeladen werden.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-1">
                <span className="block text-xs text-slate-600 mb-1">Marke *</span>
                <input
                  type="text"
                  value={addForm.make}
                  onChange={(e) => setAddForm({ ...addForm, make: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="z.B. Hyundai"
                  data-testid="admin-add-vehicle-make"
                />
              </label>
              <label className="col-span-1">
                <span className="block text-xs text-slate-600 mb-1">Modell *</span>
                <input
                  type="text"
                  value={addForm.model}
                  onChange={(e) => setAddForm({ ...addForm, model: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="z.B. Elantra"
                  data-testid="admin-add-vehicle-model"
                />
              </label>
              <label className="col-span-1">
                <span className="block text-xs text-slate-600 mb-1">Baujahr</span>
                <input
                  type="number"
                  value={addForm.year}
                  onChange={(e) => setAddForm({ ...addForm, year: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  min="1990"
                  max="2035"
                  data-testid="admin-add-vehicle-year"
                />
              </label>
              <label className="col-span-1">
                <span className="block text-xs text-slate-600 mb-1">Farbe *</span>
                <input
                  type="text"
                  value={addForm.color}
                  onChange={(e) => setAddForm({ ...addForm, color: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="z.B. Weiß"
                  data-testid="admin-add-vehicle-color"
                />
              </label>
              <label className="col-span-2">
                <span className="block text-xs text-slate-600 mb-1">Kennzeichen *</span>
                <input
                  type="text"
                  value={addForm.plate_number}
                  onChange={(e) => setAddForm({ ...addForm, plate_number: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="z.B. 6218"
                  data-testid="admin-add-vehicle-plate"
                />
              </label>
              <label className="col-span-2">
                <span className="block text-xs text-slate-600 mb-1">Fahrzeug-Typ</span>
                <select
                  value={addForm.vehicle_type}
                  onChange={(e) => setAddForm({ ...addForm, vehicle_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                  data-testid="admin-add-vehicle-type"
                >
                  {Object.entries(TYPE_LABELS).map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="col-span-1">
                <span className="block text-xs text-slate-600 mb-1">Passagiere</span>
                <input
                  type="number"
                  value={addForm.passengers}
                  onChange={(e) => setAddForm({ ...addForm, passengers: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  min="1"
                  max="60"
                  data-testid="admin-add-vehicle-passengers"
                />
              </label>
              <label className="col-span-1">
                <span className="block text-xs text-slate-600 mb-1">Gepäck</span>
                <input
                  type="number"
                  value={addForm.luggage}
                  onChange={(e) => setAddForm({ ...addForm, luggage: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  min="0"
                  max="60"
                  data-testid="admin-add-vehicle-luggage"
                />
              </label>
              <label className="col-span-2">
                <span className="block text-xs text-slate-600 mb-1">Status nach Anlegen</span>
                <select
                  value={addForm.status}
                  onChange={(e) => setAddForm({ ...addForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                  data-testid="admin-add-vehicle-status"
                >
                  <option value="approved">Sofort freigeben</option>
                  <option value="pending">Ausstehend (später prüfen)</option>
                </select>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setShowAddModal(false)}
                disabled={addingVehicle}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                data-testid="admin-add-vehicle-cancel"
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddVehicle}
                disabled={addingVehicle}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="admin-add-vehicle-submit"
              >
                {addingVehicle ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Fahrzeug anlegen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
